import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { MaterialSymbol, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideTag } from 'lucide-react';

import {
  InventoryItemsModal,
  InventoryTagData,
} from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $updateLineItemTag,
  UpdateLineItemTagInput,
} from '../_state';
import { ItemFieldProps } from '../_types';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  SelectableInventoryTags,
  SelectInventoryTagTouch,
} from '~/shared/components/select-fields/SelectInventoryTagsTouch';
import { useUIPreferences } from '~/shared/hooks';

const TAG_ICON = <LucideTag size={16} />;

//
// ⚛️ ⚛️ ⚛️ INVENTORY ITEM TAG FIELD ⚛️ ⚛️ ⚛️
//
export const InventoryItemTagField: React.FC<
  ItemFieldProps & { onAfterSelect?: () => void }
> = ({ lineItem, disabled, onUpdateItem, onKeyDown, onAfterSelect }) => {
  // Translations
  const t = useTranslations();
  const labels = t.scaleTicketPro.inventoryItemField;

  // States
  const [tagModalOpen, setTagModalOpen] = React.useState(false);

  // Atoms
  const updateLineItemTag = useSetAtom($updateLineItemTag);
  const inventoryItemsById = useAtomValue($inventoryItemsById);
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );

  // Data
  const { touchModeEnabled } = useUIPreferences();
  const modalStyle = useDialogModalStyle();
  const resolvedItem = lineItem.itemId
    ? inventoryItemsById[lineItem.itemId]
    : undefined;
  const groupStyle = useInventoryGroupStyle({
    colorHue:
      (resolvedItem?.metadata as { itemGroup?: { colorHue?: number | null } })
        ?.itemGroup?.colorHue ?? null,
  });

  // Callbacks
  const applyTagUpdate = React.useCallback(
    (input: Omit<UpdateLineItemTagInput, 'lineItemId'>) => {
      updateLineItemTag({ lineItemId: lineItem.id, ...input });
    },
    [updateLineItemTag, lineItem.id],
  );

  const handleTouchSelectTagChange = React.useCallback(
    (_value: string | undefined, entity?: SelectableInventoryTags) => {
      if (!entity) return;
      applyTagUpdate({
        tagId: entity.id,
        itemId: entity.item?.id,
        grossQuantity: entity.grossQuantity ?? 0,
        tare: entity.tare ?? 0,
        netQuantity: entity.netQuantity ?? 0,
        effectivePricingUnit: entity.item.effectivePricingUnit,
      });
      onAfterSelect?.();
    },
    [applyTagUpdate, onAfterSelect],
  );

  const handleModalTagSelect = React.useCallback(
    (_tagId: string, tagData: InventoryTagData) => {
      applyTagUpdate({
        tagId: tagData.id,
        itemId: tagData.item.id,
        grossQuantity: tagData.grossQuantity ?? 0,
        tare: tagData.tare ?? 0,
        netQuantity: tagData.netQuantity ?? 0,
        effectivePricingUnit: tagData.item.effectivePricingUnit as string,
      });
      setTagModalOpen(false);
      onAfterSelect?.();
    },
    [applyTagUpdate, onAfterSelect],
  );

  const handleModalMaterialSelect = React.useCallback(
    (materialId: string) => {
      const item = inventoryItemsById[materialId];
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onUpdateItem?.(lineItem.id, {
        itemId: materialId,
        tagId: null,
        pricingUnit: unit,
      });
      setTagModalOpen(false);
    },
    [inventoryItemsById, onUpdateItem, lineItem.id],
  );

  const handleTouchTriggerClick = React.useCallback(() => {
    setTagModalOpen(true);
  }, []);

  const handleModalOpenChange = React.useCallback((isOpen: boolean) => {
    if (!isOpen) setTagModalOpen(false);
  }, []);

  return (
    <Row
      xCenter
      gap2
      className="@InventoryItemTagField h-full"
      onKeyDown={onKeyDown}
    >
      <MaterialSymbol
        description={resolvedItem?.mnemonic}
        style={groupStyle}
        className="w-[35px] max-w-[35px]"
        symbolIcon={TAG_ICON}
      />

      <SelectInventoryTagTouch
        className="h-full min-h-full flex-1"
        placeholder={labels.placeholder}
        value={lineItem.tagId ?? undefined}
        onChange={handleTouchSelectTagChange}
        isDisabled={disabled}
        onTriggerClick={touchModeEnabled ? handleTouchTriggerClick : undefined}
      />

      {touchModeEnabled && (
        <InventoryItemsModal
          isOpen={tagModalOpen}
          mode="tags"
          onOpenChange={handleModalOpenChange}
          onSelectMaterial={handleModalMaterialSelect}
          onSelectInventoryTag={handleModalTagSelect}
          modalStyle={modalStyle}
          initialItemId={lineItem.itemId}
          initialSelectedItemId={lineItem.tagId}
          highlightedMaterialIds={highlightedMaterialIds}
          highlightedSectionTitle={highlightedSectionTitle}
        />
      )}
    </Row>
  );
};
